import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import { formatLatexPreview as formatNumLatex } from "../../../../components/inputs/RootFractionInput";

/* ===== helpers ===== */
const coeffX = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
const withSign = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);
const toLatexNumber = (raw) => (formatNumLatex(String(raw), "") || String(raw));

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) { const t = a % b; a = b; b = t; }
  return a || 1;
};

/** skrócony ułamek z dodatnim mianownikiem */
function simplifyFrac(num, den) {
  if (den < 0) { num = -num; den = -den; }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

/** LaTeX ułamka niewłaściwego (znak wyciągnięty) */
function improperLatexFromFrac(num, den) {
  const { num: N, den: D } = simplifyFrac(num, den);
  const sign = N < 0 ? "-" : "";
  return `${sign}\\dfrac{${Math.abs(N)}}{${D}}`;
}

/** LaTeX liczby mieszanej jeśli ma sens; w p.p. null */
function mixedLatexFromFrac(num, den) {
  const { num: N, den: D } = simplifyFrac(num, den);
  if (N % D === 0) return null;
  const A = Math.floor(Math.abs(N) / D);
  const R = Math.abs(N) % D;
  if (A === 0) return null;
  const sign = N < 0 ? "-" : "";
  return `${sign}${A}\\;\\dfrac{${R}}{${D}}`;
}

/* ===== generate ===== */
export function generateTask() {
  const pick = (min, max) =>
    (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * (max - min + 1)) + min);

  let c = 0; while (c === 0) c = pick(1, 6);
  const a = pick(1, 6);
  const b = pick(1, 10);
  const d = pick(1, 10);

  const leftNum = `${coeffX(a)} ${withSign(b)}`;
  const leftDen = `${coeffX(c)} ${withSign(d)}`;
  const latex = `\\dfrac{${leftNum}}{${leftDen}} = 0`;

  const { num: x0n, den: x0d } = simplifyFrac(-d, c);
  return {
    type: "RationalDomainLinearTask",
    data: { a, b, c, d, latex, x0_num: x0n, x0_den: x0d, x0: x0n / x0d },
  };
}

/* ===== task ===== */
export function renderTask({ data }) {
  return <p>Podaj dziedzinę równania: <InlineMath math={data.latex} /></p>;
}

/* ===== input ===== */
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <IntervalInput
        value={userInput?.D || ""}
        onChange={(val) => onChange({ ...userInput, D: val })}
        disabled={disabled}
        label="D"
        placeholder="np. ℝ\{3}  lub  ℝ\{3, 5}  lub  ℝ - {3}"
        asString
        previewWithX
        prefixAbove
      />
    </div>
  );
}

/* ===== check (tolerancja ±0.01 dla dziesiętnych) ===== */
export function checkAnswer({ data }, userInput) {
  const raw = typeof userInput?.D === "string" ? userInput.D : (userInput?.D?.raw || "");
  const parsed = parseIntervalInput(raw);
  const x0 = data.x0;
  const eqTol = (x) => Math.abs(x - x0) <= 0.01 + 1e-9;

  if (!parsed) return { correct: false, expected: x0 };

  if (parsed.kind === "complement") {
    return { correct: eqTol(parsed.point), expected: x0 };
  }

  if (parsed.kind === "intervals") {
    const ints = parsed.intervals;
    if (ints.length !== 2) return { correct: false, expected: x0 };
    const [I, J] = ints;

    const ok1 =
      I.left === -Infinity && !I.rightClosed && eqTol(I.right) &&
      eqTol(J.left) && !J.leftClosed && J.right === Infinity;

    const ok2 =
      J.left === -Infinity && !J.rightClosed && eqTol(J.right) &&
      eqTol(I.left) && !I.leftClosed && I.right === Infinity;

    return { correct: ok1 || ok2, expected: x0 };
  }

  return { correct: false, expected: x0 };
}

/* ===== short answer ===== */
export function renderAnswer({ data }) {
  const { x0_num, x0_den } = data;
  const imp = improperLatexFromFrac(x0_num, x0_den);
  const mix = mixedLatexFromFrac(x0_num, x0_den);
  const finalPoint = mix || imp; // <- ostatecznie pokazujemy mieszany, jeśli istnieje

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <InlineMath math={`x\\in\\;\\mathbb{R}\\setminus\\{${finalPoint}\\}`} />
    </div>
  );
}

/* ===== user answer preview ===== */
export function renderUserAnswer(userInput) {
  const raw = typeof userInput === "string"
    ? userInput
    : (userInput?.D?.raw || userInput?.D || "");
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const latex = intervalToLatex(raw, { withX: true });
  return <InlineMath math={`\\displaystyle ${latex}`} />;
}

/* ===== explanation ===== */
export function renderExplanation({ data }) {
  const { c, d, x0_num, x0_den } = data;

  const dL = toLatexNumber(d);
  const cL = toLatexNumber(c);
  // minus minus → plus
  const opSubD = d < 0 ? `+\\,${toLatexNumber(-d)}` : `-\\,${dL}`;
  const opDivC = c < 0 ? `:\\,(${cL})` : `:\\,${cL}`;

  const imp = improperLatexFromFrac(x0_num, x0_den);
  const mix = mixedLatexFromFrac(x0_num, x0_den); // może być null
  const finalPoint = mix || imp; // <- w zapisie końcowym użyjemy formatu mieszanego

  // transformacje (dwie linie z pionowymi kreskami)
  const transformLines = String.raw`\begin{aligned}
    ${coeffX(c)} ${withSign(d)} &\neq 0  &\big|&\; ${opSubD} \\
    ${coeffX(c)} &\neq ${toLatexNumber(-d)} &\big|&\; ${opDivC}
  \end{aligned}`;

  // wartość krytyczna: osobny blok 1–2 linie (niewłaściwy, a niżej mieszany jeśli jest)
  const criticalLines = mix
    ? String.raw`\begin{aligned}
        x &= ${imp} \\
        x &= ${mix}
      \end{aligned}`
    : String.raw`x = ${imp}`;

  return (
    <div className="space-y-4">
      <p><strong>Krok 1:</strong> Warunek z mianownika.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`${coeffX(c)} ${withSign(d)} \\neq 0`} />
      </div>

      <p><strong>Krok 2:</strong> Rozwiązujemy warunek (pionowa kreska = wykonana operacja).</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={transformLines} />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={criticalLines} />
      </div>

      <p><strong>Krok 3:</strong> Zapis dziedziny.</p>
      <div style={{ textAlign: "center", marginBottom: "0.25rem" }}>
        {/* tutaj używamy formatu mieszanego (jeśli występuje) */}
        <BlockMath math={`\\mathbb{R}\\setminus\\{${finalPoint}\\}`} />
      </div>
      <div style={{ textAlign: "center", marginTop: "0.25rem" }}>
        <strong>Równoważnie:</strong>
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`(-\\infty, ${finalPoint})\\,\\cup\\,(${finalPoint},\\,\\infty)`} />
      </div>
    </div>
  );
}
